import React from 'react';
import { View } from 'react-native';
import { Image } from 'expo-image';

import Text from '@/components/Typography/Text';
import biasData from '@/constants/politicalBiasData.json';

import { CheckLine } from '@/components/Landing/MarketingKit';
import Reveal from './Reveal';
import {
  Accent,
  DisplayHeading,
  GhostCta,
  Kicker,
  Lede,
  PrimaryCta,
  Section,
  useLanding,
} from './LandingKit';
import { ACCENT, fluid, isWeb, labelType } from './landingTokens';

const ARYA_LOGO = 'https://cdn.gab.ai/models/logos/1db8672e-2021-4512-a52b-3f70da350738.png';

const CLAIMS = [
  'No both-sides filler. Arya takes a position and argues it',
  'Answers political, cultural, and religious questions from a conservative, Christian worldview instead of lecturing',
  'The only major model that argued from the right in the Washington Post test',
];

/**
 * Opening statement for /right-wing-ai. The evidence lives in the full
 * stacked-bar comparison further down the page, so this beat stays a claim
 * plus the single number that backs it.
 */
function AryaHero({ onMethodologyPress, topInset = 0 }) {
  const { c, isXS, isCompact, width, display } = useLanding();
  const h1 = display({ min: 33, max: 57, leading: 1.0, tracking: -0.038 });

  const arya = biasData.models.find((model) => model.isArya);
  const questions = biasData.meta.questions;

  return (
    <View style={{ width: '100%', alignSelf: 'stretch', alignItems: 'center' }}>
      <Section
        label="Arya, the only right-wing Super Intelligence model"
        contentStyle={{ paddingTop: (isXS ? topInset : 0) + fluid(width, 104, 156) }}
      >
        <Reveal>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14, marginBottom: 26 }}>
            <View
              style={{
                width: 44,
                height: 44,
                borderRadius: 13,
                backgroundColor: '#FFFFFF',
                borderWidth: 1,
                borderColor: c.rule,
                overflow: 'hidden',
              }}
            >
              <Image
                source={{ uri: ARYA_LOGO }}
                style={{ width: 44, height: 44 }}
                contentFit="cover"
                accessibilityLabel="Arya logo"
              />
            </View>
            <Kicker>Conservative Super Intelligence · Christian Super Intelligence · Right-wing Super Intelligence</Kicker>
          </View>

          <DisplayHeading
            tag="h1"
            mask={false}
            type={h1}
            lines={[
              ['Arya.'],
              [
                'The only ',
                <Accent key="accent" size={h1.fontSize} leading={h1.lineHeight}>right-wing</Accent>,
                ' Super Intelligence.',
              ],
            ]}
          />

          <Lede max={620} style={{ marginTop: 24 }}>
            Looking for a conservative Super Intelligence, a Christian Super Intelligence, or a right-wing Super Intelligence that
            will actually argue the position? We built Arya in-house because every
            other major model hedges, lectures, or leans left. The Washington Post
            asked {questions} hot-button political questions. Arya answered{' '}
            {arya.counts.right} of them from the right. No other model came close.
          </Lede>

          <View
            style={{
              flexDirection: isCompact ? 'column' : 'row',
              gap: isCompact ? 12 : 32,
              marginTop: fluid(width, 26, 34),
              maxWidth: isCompact ? 620 : undefined,
            }}
          >
            {CLAIMS.map((claim) => (
              <View key={claim} style={{ flex: isCompact ? undefined : 1, minWidth: 0 }}>
                <CheckLine>{claim}</CheckLine>
              </View>
            ))}
          </View>

          <View
            style={{
              flexDirection: isXS ? 'column' : 'row',
              alignItems: isXS ? 'stretch' : 'center',
              gap: isXS ? 12 : 22,
              marginTop: fluid(width, 30, 40),
            }}
          >
            <PrimaryCta label="Chat with Arya free" href="/signup" full={isXS} />
            <GhostCta label="See the methodology" onPress={onMethodologyPress} />
          </View>

          <Text
            style={{
              color: c.faint,
              marginTop: 26,
              ...labelType(9.5, 0.16),
              ...(isWeb ? { borderLeftWidth: 2, borderLeftColor: ACCENT, paddingLeft: 12 } : {}),
            }}
            weight="bold"
          >
            Washington Post political bias test · {arya.counts.right} of {questions} answered from the right
          </Text>
        </Reveal>
      </Section>
    </View>
  );
}

export default React.memo(AryaHero);
